import { Node as PmNode, ResolvedPos } from 'prosemirror-model'
import { EditorState } from 'prosemirror-state'

import { INLINE_PAGE_BREAK } from '@showrunner/codex'
import { constants } from '@showrunner/prose-schemas'
import { schemas, ZInfer } from '@showrunner/scrapi'

import {
  isRejectedAddition,
  isUnrejectedDeletion,
} from '#choo-app/lib/editor/plugins/change-tracking/helpers'
import { getBlockReservedCharacters } from '#layouts/Script/buildFormatStyles'
import { DatadogClient } from '#util/datadog'
import { BlockFormats, FormatBlockName } from '#util/formats'
import { Unit } from '#util/Unit'

type DualFormatBlockConfig = ZInfer<
  typeof schemas.scriptFormats.DualBlockConfig
>

const ddLog = DatadogClient.getInstance()

const { NodeTypeMap } = constants

// ignore inline text nodes based on their marks
const ignoreInlineText = ($pos: ResolvedPos) =>
  $pos.marks().some((m) => isRejectedAddition(m) || isUnrejectedDeletion(m))

// this is admittedly the cheap/lazy option. it would be more correct to
// traverse each char and confirm that the relevant mark spans the entire node
const ignoreEntireNode = (node: PmNode): boolean => {
  const marks = node.resolve(0).marks()
  const endMarks = node.resolve(node.content.size).marks()

  if (marks.some(isRejectedAddition) && endMarks.some(isRejectedAddition)) {
    return true
  }
  if (marks.some(isUnrejectedDeletion) && endMarks.some(isUnrejectedDeletion)) {
    return true
  }

  return false
}

// TODO: canonize
export const LOCKED = 'locked'
export const LOCKED_MANUAL = 'locked-manual'

export function hasLockedPages(state: EditorState): boolean {
  return containsLockedPageBreak(state.doc)
}

function containsLockedPageBreak(node: PmNode): boolean {
  return isLockedPageBreak(node) || node.children.some(containsLockedPageBreak)
}

// isLockedPageBreak checks if a node has a locked page break attribute
export const isLockedPageBreak = (node: PmNode) => {
  const pb = node.attrs?.pageBreak
  return (
    typeof pb === 'string' &&
    (pb.startsWith(`${LOCKED}:`) || pb.startsWith(`${LOCKED_MANUAL}:`))
  )
}

export const getLockedPageNumber = (node: PmNode): string | undefined => {
  const pageBreak = node.attrs.pageBreak
  if (!pageBreak || typeof pageBreak !== 'string') return undefined
  const [kind, ...parts] = pageBreak.split(':')

  if ((kind === LOCKED || kind === LOCKED_MANUAL) && parts.length) {
    // This handles the case where someone includes ":" in their page name
    return parts.join(':')
  }
}

export const isManualBreak = (node: PmNode) =>
  node.attrs.pageBreak === INLINE_PAGE_BREAK.manual || isLockedPageBreak(node)

// Info we can determine by looking at a 2nd level node and
// the script format
type DirectBlockInfo = {
  blockId: string
  blockType: string
  contentHeight: number
  // sections of line-broken text for this block
  // each section represents text between hard breaks
  // for a unbroken text block, there will be only one section
  sections?: Section[]
  spaceAbove: number
  manualBreak: boolean
  lockedPageBreak: boolean
  pageAlias?: string
  omitFromPagination?: boolean
  left?: Unit
  right?: Unit
}

// a section is an array of lines without hard breaks
export type Section = Line[]

export type Line = {
  text: string
  width: number
  from: number
  to: number
}

// Combine the previous with doc position info so we can
// add decorations or mutate in appendTransaction
export type PositionedBlockInfo = DirectBlockInfo & {
  startPos: number
  nodeSize: number
}

// We want to get the script format config for a block. Our prosemirror schema
// isn't typed so we swap in some reasonable defaults and log an error if
// we can't resolve a config (it indicates a bug)
type ConfigData = {
  width: number
  lineHeight: number
  blockTopMargin: number
  marginLeft: number
}
const DEFAULT_CONFIG: ConfigData = {
  width: 50,
  lineHeight: 1,
  blockTopMargin: 0,
  marginLeft: 0,
}
const getBlockConfig = (
  node: PmNode,
  blockFormats: BlockFormats,
  inDual?: boolean,
): ConfigData => {
  const config: ValueOf<BlockFormats> | undefined =
    blockFormats[node.type.name as FormatBlockName]

  if (!config) {
    ddLog.errorOnce(
      'Failed to find block config',
      {
        topic: 'inlinePagination',
        nodeType: node.type.name,
        inDual,
      },
      'block-config-lookup',
    )
  }

  const forcedConfig = config ?? DEFAULT_CONFIG

  if (!inDual) {
    return forcedConfig
  }

  const result = (forcedConfig as DualFormatBlockConfig).dualDialogOverrides

  if (!result) {
    ddLog.errorOnce(
      'Failed to find dual format config',
      { type: node.type.name, topic: 'inlinePagination' },
      'dual-block-config-lookup',
    )
  }

  return result ?? { ...DEFAULT_CONFIG, width: DEFAULT_CONFIG.width / 2 }
}

// left/right margin of a block, in ch units from the page's own margins —
// same values getTextBlockHeightInfo derives, without the line-wrapping
// measurement work that comes with a full getBlockHeightInfo call
export function getBlockMargins(
  node: PmNode,
  blockFormats: BlockFormats,
  inDual?: boolean,
): { left: Unit; right: Unit } {
  const config = getBlockConfig(node, blockFormats, inDual)
  const effectiveWidth = node.attrs.width ?? config.width
  const effectiveMarginLeft = node.attrs.marginLeft ?? config.marginLeft

  return {
    left: new Unit(effectiveMarginLeft, 'ch'),
    right: new Unit(effectiveMarginLeft + effectiveWidth, 'ch'),
  }
}

// Count the lines of text (including hard breaks) for a Text block
const getTextBlockHeightInfo = (
  node: PmNode,
  blockFormats: BlockFormats,
  inDual?: boolean,
  offset?: number,
): DirectBlockInfo => {
  const config = getBlockConfig(node, blockFormats, inDual)

  const blockId = node.attrs.id
  const blockType = node.type.name
  const manualBreak = isManualBreak(node)
  const lockedPageBreak = isLockedPageBreak(node)
  const pageAlias = getLockedPageNumber(node)

  const blockWidth = node.attrs.width ?? config.width
  const effectiveWidth = Math.max(
    1,
    blockWidth - getBlockReservedCharacters(blockType),
  )
  const { left, right } = getBlockMargins(node, blockFormats, inDual)

  const common = {
    blockId,
    blockType,
    lockedPageBreak,
    manualBreak,
    pageAlias,
    left,
    right,
  }

  // some tracked changes should be omitted from pagination calculations entirely
  if (ignoreEntireNode(node)) {
    return {
      ...common,
      contentHeight: 0,
      spaceAbove: 0,
      omitFromPagination: true,
    }
  }

  const textSections = getNodeTextSections(node, offset)

  // for each section, determine how many lines it would take to render it
  const sections: Section[] = textSections.map((text) => {
    return wrapTextWithPreserveWhitespace(text, effectiveWidth, {
      breakLongWords: true,
      hyphenate: false,
    })
  })

  const effectiveLineCount = sections.reduce((a, b) => a + b.length, 0)
  const effectivelineHeight = node.attrs.lineHeight ?? config.lineHeight
  const contentHeight = effectiveLineCount * effectivelineHeight
  const spaceAbove = node.attrs.blockTopMargin ?? config.blockTopMargin

  return { ...common, contentHeight, spaceAbove, sections }
}

// adding a cache to avoid expensive text concatination for a node
const sectionCache = new WeakMap<PmNode, Map<number, string[]>>()
const getNodeTextSections = (node: PmNode, pos = 0): string[] => {
  const posCache = sectionCache.get(node) ?? new Map()
  if (posCache.has(pos)) {
    return posCache.get(pos)!
  }

  // a textblock is broken into sections by hard breaks
  // when there is no hard break, there is only one section
  const textSections: string[] = ['']
  // gather text content into sections based on hard breaks
  node.forEach((inlineNode, offset) => {
    const end = offset + inlineNode.nodeSize
    if (end <= pos) {
      // this node is entirely before the position we're interested in
      return
    }

    if (inlineNode.text) {
      const $start = node.resolve(offset + 1)
      if (!ignoreInlineText($start)) {
        const charsToSkip = pos > offset ? pos - offset : 0
        textSections[textSections.length - 1] +=
          inlineNode.text.slice(charsToSkip)
      }
    } else if (inlineNode.type.name === NodeTypeMap.HARD_BREAK) {
      // start a new section on hard break, this section will be moved to the next page
      textSections.push('')
    }
  })

  posCache.set(pos, textSections)
  sectionCache.set(node, posCache)

  return textSections
}

// a simple text measurement function that approximates text width
const measureText = (text: string): number => {
  // simple approximation: width is number of characters
  return text.length
}

// Preserves whitespace including trailing spaces on each line
export function wrapTextWithPreserveWhitespace(
  text: string,
  maxWidth: number,
  options?: {
    breakLongWords: boolean
    hyphenate: boolean
  },
): Line[] {
  const { breakLongWords = true, hyphenate = false } = options ?? {}
  const lines: Line[] = []

  // Split text into tokens (words and spaces)
  // This regex captures both words and spaces separately
  const tokens: Array<{
    type: 'word' | 'space' | 'newline'
    content: string
  }> = []

  // Match sequences of non-space characters (words) and sequences of spaces.
  // We also want to match new lines to split the text
  const regex = /(?<space>[^\S\n]+)|(?<newline>\n)|(?<word>\S+)/g
  let match

  // Break the line into tokens
  while ((match = regex.exec(text)) !== null) {
    const { groups } = match
    if (groups?.space) {
      tokens.push({ type: 'space', content: groups.space })
    } else if (groups?.newline) {
      tokens.push({ type: 'newline', content: groups.newline })
    } else if (groups?.word) {
      tokens.push({ type: 'word', content: groups.word })
    }
  }

  let currentLine = ''
  let lineStart = 0

  for (const token of tokens) {
    if (token.type === 'space') {
      // Always add spaces to current line
      currentLine += token.content
      continue
    }

    if (token.type === 'newline') {
      appendLine(lines, currentLine, measureText(currentLine), lineStart)
      // +1 for the consumed '\n' which belongs to no line
      lineStart += currentLine.length + 1
      currentLine = ''
      continue
    }

    // Handle word token
    const word = token.content
    const wordWidth = measureText(word)

    // Handle long words that don't fit on a single line
    if (wordWidth > maxWidth && breakLongWords) {
      // First, flush current line if it has content
      if (currentLine) {
        appendLine(lines, currentLine, measureText(currentLine), lineStart)
        lineStart += currentLine.length
        currentLine = ''
      }

      // Break the long word into lines
      const chunks = breakLongWord(word, maxWidth, measureText, hyphenate)

      // Add all chunks except the last one as complete lines
      for (let j = 0; j < chunks.length - 1; j++) {
        const chunk = chunks[j]
        appendLine(lines, chunk, measureText(chunk), lineStart)
        lineStart += chunk.length
      }

      // Start new line with the last chunk
      currentLine = chunks[chunks.length - 1]
      continue
    }

    // Test if adding this word would exceed max width
    const testLine = currentLine + word
    const testWidth = measureText(testLine)

    if (testWidth > maxWidth && currentLine.trim()) {
      // Line would be too long, save current line and start new one
      appendLine(lines, currentLine, measureText(currentLine), lineStart)
      lineStart += currentLine.length
      currentLine = word
    } else {
      // Add word to current line
      currentLine = testLine
    }
  }

  // Add the last line if it has content or the text ended with a hard break.
  if (currentLine || text.endsWith('\n')) {
    const lineWidth = measureText(currentLine)
    appendLine(lines, currentLine, lineWidth, lineStart)
  }

  // Ensure at least one line is returned for empty text
  if (lines.length === 0) {
    appendLine(lines, '', 0)
  }

  return lines
}

const appendLine = (lines: Line[], text: string, width: number, from = 0) => {
  const to = from + text.length
  lines.push({
    text,
    width,
    from,
    to,
  })
}

// breaks a long word into chunks that fit within maxWidth
function breakLongWord(
  word: string,
  maxWidth: number,
  measureText: (text: string) => number,
  hyphenate: boolean,
): string[] {
  const chunks: string[] = []
  let currentChunk = ''
  const hyphen = hyphenate ? '-' : ''

  for (let i = 0; i < word.length; i++) {
    const char = word[i]
    const testChunk = currentChunk + char
    const testWithHyphen = testChunk + hyphen
    const testWidth = measureText(hyphenate ? testWithHyphen : testChunk)

    if (testWidth > maxWidth && currentChunk) {
      // Current chunk is full, save it
      chunks.push(currentChunk + hyphen)
      currentChunk = char
    } else {
      currentChunk = testChunk
    }
  }

  // Add remaining characters
  if (currentChunk) {
    chunks.push(currentChunk)
  }

  return chunks
}

// we measure individual text blocks OR dual dialogue sections. This takes a
// level 2 node and returns either the single textblock or the array of textblocks
// in each column of dual dialogue
type MeasureableBlock = PmNode | [PmNode[], PmNode[]]
const breakDownToMeasurable = (node: PmNode): MeasureableBlock => {
  if (node.type.name === NodeTypeMap.DUAL_DIALOGUE) {
    const column1: PmNode[] = []
    const column2: PmNode[] = []
    node.forEach((column, _, index) => {
      column.forEach((node) => {
        if (index === 0) {
          column1.push(node)
        } else {
          column2.push(node)
        }
      })
    })
    return [column1, column2]
  }
  return node
}

// adds together heights of an array of blocks, throwing
// away the space above for the first block of the page
// or dual dialogue column
export const addBlockHeights = (
  items: Array<{ contentHeight: number; spaceAbove: number }>,
): number => {
  return items.reduce((cumulative, block, index) => {
    const currentBlockHeight =
      index === 0 ? block.contentHeight : block.contentHeight + block.spaceAbove
    return cumulative + currentBlockHeight
  }, 0)
}

// for each depth 2 block in the script, we want the content height and space
// above. For dual dialogue, this means traversing into each column and summing
// the blocks.
// this function is memoized based on the arguments using a WeakMap.
// this is doable because the nodes are immutable.
export const getBlockHeightInfo = (
  node: PmNode,
  blockFormats: BlockFormats,
  offset = 0,
): DirectBlockInfo => {
  const entry = cache.get(node) ?? { blockFormats, blockInfoMap: new Map() }
  if (
    entry &&
    entry.blockFormats === blockFormats &&
    entry.blockInfoMap.has(offset)
  ) {
    return entry.blockInfoMap.get(offset)!
  }

  const measurable = breakDownToMeasurable(node)

  // not dual dialogue
  if (measurable instanceof PmNode) {
    const blockInfo = getTextBlockHeightInfo(node, blockFormats, false, offset)
    entry.blockInfoMap.set(offset, blockInfo)
    cache.set(node, entry)

    return blockInfo
  }

  // dual dialogue has two columns to measure
  const column1Height = addBlockHeights(
    measurable[0].map((block) =>
      getTextBlockHeightInfo(block, blockFormats, true),
    ),
  )
  const column2Height = addBlockHeights(
    measurable[1].map((block) =>
      getTextBlockHeightInfo(block, blockFormats, true),
    ),
  )

  const contentHeight = Math.max(column1Height, column2Height)
  const blockInfo = {
    blockId: node.attrs.id,
    blockType: NodeTypeMap.DUAL_DIALOGUE,
    manualBreak: isManualBreak(node),
    lockedPageBreak: isLockedPageBreak(node),
    contentHeight,
    spaceAbove: blockFormats.dual_dialogue.blockTopMargin,
    pageAlias: getLockedPageNumber(node),
  }

  entry.blockInfoMap.set(offset, blockInfo)
  cache.set(node, entry)
  return blockInfo
}

const cache: WeakMap<
  PmNode,
  { blockFormats: BlockFormats; blockInfoMap: Map<number, DirectBlockInfo> }
> = new WeakMap()

export const isCharacterFollower = (blockInfo: DirectBlockInfo) => {
  return (
    blockInfo.blockType === NodeTypeMap.DIALOGUE ||
    blockInfo.blockType === NodeTypeMap.PARENTHETICAL
  )
}

export const extractBlockInfo = ({
  doc,
  blockFormats,
}: {
  doc: PmNode
  blockFormats: BlockFormats
}): {
  pageBlocks: PositionedBlockInfo[]
  titlePageBlocks: PositionedBlockInfo[]
} => {
  const orderedPageBlocks: PositionedBlockInfo[] = []
  const orderedTitlePageBlocks: PositionedBlockInfo[] = []
  doc.descendants((node, pos, parent) => {
    const isPage = parent?.type.name === NodeTypeMap.PAGE
    const isTitlePage = parent?.type.name === NodeTypeMap.TITLE_PAGE

    if (isPage || isTitlePage) {
      const blockData: PositionedBlockInfo = {
        ...getBlockHeightInfo(node, blockFormats),
        startPos: pos,
        nodeSize: node.nodeSize,
      }

      if (isPage) {
        orderedPageBlocks.push(blockData)
      }

      if (isTitlePage) {
        orderedTitlePageBlocks.push(blockData)
      }

      // don't traverse below depth 2
      return false
    }
  })
  return {
    pageBlocks: orderedPageBlocks,
    titlePageBlocks: orderedTitlePageBlocks,
  }
}
